import JSZip from "jszip";

import { normalizeCropCode } from "pages/admin/experiments/cropExtraction/cropCodes";
import { directChildElements, findXmlInZip, findYearInXml, parseXml, xmlNumber, xmlText } from "pages/admin/experiments/cropExtraction/parsers/xmlHelpers";
import { CropRecord, ParserResult } from "pages/admin/experiments/cropExtraction/types";

export const parseElanNrw = async (jszip: JSZip): Promise<ParserResult> => {
  // We don't pin the filename (`NWxxAGR_*.xml`) because the same data-experts
  // schema is used for other federal states with different XML name prefixes.
  // Skip `*.nn.xml` to avoid collision with the AntDV parser.
  const found = await findXmlInZip(jszip, (rel) => {
    const lc = rel.toLowerCase();
    return lc.endsWith(".xml") && !lc.endsWith(".nn.xml");
  });
  if (!found) return { method: "xml_parsing", crops: [], warnings: ["Could not find ELAN-NRW <nn> XML"] };

  const { doc, error } = parseXml(found.xml);
  if (error) return { method: "xml_parsing", crops: [], warnings: [`XML parser error: ${error}`] };

  const crops: CropRecord[] = [];
  const parzellen = Array.from(doc.getElementsByTagNameNS("*", "parzelle"));

  for (const parzelle of parzellen) {
    const flik = xmlText(parzelle, "feldblock");
    const teilschlag = xmlText(parzelle, "teilschlag");
    const schlag = directChildElements(parzelle, "schlag")[0];
    const fieldName = schlag ? xmlText(schlag, "bezeichnung") : "";
    const schlagNummer = schlag ? xmlText(schlag, "nummer") : "";

    // Prefer the current-year `nutzungaj`, fall back to `vorjahr` if missing.
    const nutzungContainer = directChildElements(parzelle, "nutzungaj")[0] ?? directChildElements(parzelle, "nutzungvj")[0];
    const cropCode = nutzungContainer ? normalizeCropCode(xmlText(nutzungContainer, "code")) : "";
    // Inline NRW description (e.g. "411 - Silomais") is more authoritative
    // than our generic dictionary, so prefer it but drop the leading code.
    const inlineDescription = (nutzungContainer ? xmlText(nutzungContainer, "bezeichnung") : "").replace(/^\s*\d+\s*-\s*/, "").trim();

    const m2 = xmlNumber(parzelle, "nettoflaeche") || xmlNumber(parzelle, "fbflaeche");

    crops.push({
      fieldId: teilschlag && schlagNummer ? `${schlagNummer}/${teilschlag}` : schlagNummer || flik,
      fieldName: fieldName || schlagNummer || flik,
      cropCode,
      cropDescription: inlineDescription,
      areaHa: m2 > 0 ? m2 / 10000 : 0,
      fieldNumber: schlagNummer || undefined,
    });
  }

  const warnings = crops.length === 0 ? ["No <parzelle> entries with crop codes were found"] : [];
  return { method: "xml_parsing", crops, warnings, year: findYearInXml(doc) };
};
